import type { BankTransfer, BankTransferStatus } from '@/lib/bank-transfers-client';
import type { OrderBoardCard } from '@/lib/order-board-client';
import type { Order, OrderStatus } from '@/lib/orders-client';

/**
 * The unverified-receipt flag (pure). Any receipt places a transfer order, so on a brand with no POS
 * the operator UI is the only place the store learns the receipt was not cleared before it prepares
 * the order — a POS brand carries the warning in its POS note instead, which is why every caller
 * gates this on the brand having no enabled POS (see `use-transfer-review.ts`).
 */

/** The receipt statuses that still need a human: the reader did not clear them. */
export const TRANSFER_REVIEW_STATUSES: BankTransferStatus[] = ['pending', 'mismatch'];

/**
 * Not "placed and still in the store's hands": never placed (draft, abandoned, superseded), or
 * already over (cancelled, completed) — there is nothing left to prepare.
 */
const NOT_UNDER_REVIEW: OrderStatus[] = ['draft', 'abandoned', 'superseded', 'cancelled', 'completed'];

/** A placed transfer order still in play — the only kind the flag can apply to. */
export function isTransferReviewCandidate(order: { status: OrderStatus; paymentPreference?: string | null }): boolean {
  return order.paymentPreference === 'bank_transfer' && !NOT_UNDER_REVIEW.includes(order.status);
}

/** The receipt is `pending` or `mismatch` and no operator vouched for the payment by hand. */
function receiptUnverified(transfer: Pick<BankTransfer, 'status' | 'verification'>): boolean {
  return TRANSFER_REVIEW_STATUSES.includes(transfer.status) && !transfer.verification?.manualVerification;
}

/** The order detail: the full order already embeds its transfer. */
export function needsTransferReview(order: Pick<Order, 'status' | 'paymentPreference' | 'bankTransfer'>): boolean {
  return isTransferReviewCandidate(order) && !!order.bankTransfer && receiptUnverified(order.bankTransfer);
}

/**
 * The board: its cards carry no transfer, so they are joined against the brand's transfers listed
 * by status. A superseded transfer no longer speaks for its order (a newer receipt replaced it), and
 * a transfer never linked to a menteo order has no card to mark.
 *
 * Returns the ids of the cards to badge — only those of a brand in `brandsWithoutPos`.
 */
export function transferReviewOrderIds(
  cards: readonly OrderBoardCard[],
  transfers: readonly BankTransfer[],
  brandsWithoutPos: ReadonlySet<string>,
): Set<string> {
  const unverified = new Set<string>();
  for (const transfer of transfers) {
    if (transfer.orderId && !transfer.supersededAt && receiptUnverified(transfer)) unverified.add(transfer.orderId);
  }

  const ids = new Set<string>();
  for (const card of cards) {
    if (
      card.brandId &&
      brandsWithoutPos.has(card.brandId) &&
      isTransferReviewCandidate(card) &&
      unverified.has(card.orderId)
    ) {
      ids.add(card.orderId);
    }
  }
  return ids;
}

/** Explicit light/dark pairs — the banner and the badge share one amber tint. */
export const TRANSFER_REVIEW_TINT =
  'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300';
